import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import {
  environment,
  resolveLocalBaseUrl,
  resolveLocalPath,
} from '@g99-product-web-ui/shared-environment';
import {
  API_ENDPOINTS,
  LOCAL_STORAGE_KEYS,
} from '@g99-product-web-ui/shared-util-lib';
import { Subscription, fromEvent, interval, merge } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ServiceType } from '../../constants/service-types.enum';
import { HttpService } from '../../core/http-service/http.service';

/** How often the buffer is flushed while the tab is open. */
const FLUSH_INTERVAL_MS = 10_000;

/** How often the session is touched so an abandoned tab can be told from an idle one. */
const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Hard ceiling on the buffer. Reached only if the network is down for a long stretch; past this the
 * oldest events are dropped, because holding an unbounded array in a long-lived tab is a worse
 * failure than losing the start of a browsing trail.
 */
const MAX_BUFFERED_EVENTS = 200;

interface ITrackedEvent {
  eventUid: string;
  eventType: 'PAGE_VIEW';
  path: string;
  url: string;
  pageName: string;
  referrerPath: string | null;
  sequenceNo: number;
  enteredAt: string;
}

/**
 * Records which pages a user visits, against the session they visited them in.
 *
 * Separate from `AnalyticsProxyService`, which forwards the same navigations to Amplitude. Both run:
 * the Amplitude path still backs the existing Application Analytics report, while this one writes to
 * our own database where the rows can be joined to businesses, plans and users.
 *
 * Three things this service will not do, by design:
 * - it never throws into the application; a tracking failure is swallowed and the page carries on
 * - it never blocks a navigation; events are buffered and posted on a timer
 * - it never trusts itself for identity; the server takes user and tenant from the JWT
 */
@Injectable({ providedIn: 'root' })
export class SessionTrackingService implements OnDestroy {
  private _subscriptions: Subscription | null = null;
  private _buffer: ITrackedEvent[] = [];
  private _lastPath: string | null = null;
  private _flushing = false;

  constructor(
    private readonly _httpService: HttpService,
    private readonly _router: Router
  ) {}

  ngOnDestroy(): void {
    this.stop();
  }

  /**
   * Begins tracking. Safe to call more than once — a second call is ignored rather than doubling
   * every event, because both the login flow and the session restore path call it.
   */
  public start(): void {
    if (this._subscriptions) {
      return;
    }

    this._openSession();

    const subs = new Subscription();

    subs.add(
      this._router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd
          )
        )
        .subscribe((event) => this._recordPageView(event))
    );

    subs.add(interval(FLUSH_INTERVAL_MS).subscribe(() => this._flush(false)));

    subs.add(
      interval(HEARTBEAT_INTERVAL_MS).subscribe(() => this._openSession())
    );

    // pagehide fires where unload is unreliable (mobile Safari especially); visibilitychange
    // catches a tab being backgrounded, which on mobile is often the last event we ever get.
    subs.add(
      merge(
        fromEvent(document, 'visibilitychange'),
        fromEvent(window, 'pagehide')
      ).subscribe(() => {
        if (document.visibilityState === 'hidden') {
          this._flush(true);
        }
      })
    );

    this._subscriptions = subs;
  }

  /** Stops tracking and closes the session. Called on logout. */
  public stop(reason = 'logout'): void {
    this._subscriptions?.unsubscribe();
    this._subscriptions = null;
    this._flush(true);
    this._endSession(reason);
    this._lastPath = null;
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ANALYTICS_SESSION_KEY);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ANALYTICS_SESSION_SEQ);
  }

  /* -------------------- session lifecycle -------------------- */

  /**
   * The session key lives in `localStorage`, not `sessionStorage`.
   *
   * That makes one browser profile one session, which is what "logged in on two laptops" means.
   * `sessionStorage` is per tab, so opening a second tab would mint a second session and inflate
   * every session count on the report.
   */
  private _sessionKey(): string {
    const existing = localStorage.getItem(
      LOCAL_STORAGE_KEYS.ANALYTICS_SESSION_KEY
    );
    if (existing) {
      return existing;
    }
    const key = this._uuid();
    localStorage.setItem(LOCAL_STORAGE_KEYS.ANALYTICS_SESSION_KEY, key);
    return key;
  }

  /**
   * Next position in the session, kept in local storage beside the session key.
   *
   * It cannot live in memory: the key survives a page refresh but the service instance does not,
   * so an in-memory counter restarts at 1 inside the same session and every reload produces a
   * second event numbered 1. Both values have to persist together or neither is meaningful.
   */
  private _nextSequenceNo(): number {
    let next = 1;
    try {
      const stored = Number(
        localStorage.getItem(LOCAL_STORAGE_KEYS.ANALYTICS_SESSION_SEQ)
      );
      next = Number.isFinite(stored) && stored > 0 ? stored + 1 : 1;
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.ANALYTICS_SESSION_SEQ,
        String(next)
      );
    } catch {
      // Storage unavailable (private mode, blocked cookies): the server orders by enteredAt
      // anyway, so a wrong number is cosmetic.
    }
    return next;
  }

  /** Opens the session, and doubles as the heartbeat — the endpoint is an upsert. */
  private _openSession(): void {
    const ua = navigator.userAgent;
    this._httpService
      .post(API_ENDPOINTS.SESSION_ANALYTICS.sessionStart, {
        sessionKey: this._sessionKey(),
        userAgent: ua,
        deviceType: this._deviceType(ua),
        browser: this._browser(ua),
        os: this._os(ua),
      })
      .subscribe({ error: () => undefined });
  }

  private _endSession(reason: string): void {
    const body = JSON.stringify({
      sessionKey: this._sessionKey(),
      reason,
    });
    this._sendOnUnload(API_ENDPOINTS.SESSION_ANALYTICS.sessionEnd, body);
  }

  /* -------------------- page views -------------------- */

  private _recordPageView(event: NavigationEnd): void {
    const url = event.urlAfterRedirects || event.url;
    const path = this._normalisedPath();

    this._buffer.push({
      eventUid: this._uuid(),
      eventType: 'PAGE_VIEW',
      path,
      url,
      pageName: this._pageName(path, url),
      referrerPath: this._lastPath,
      sequenceNo: this._nextSequenceNo(),
      enteredAt: new Date().toISOString(),
    });

    this._lastPath = path;

    if (this._buffer.length > MAX_BUFFERED_EVENTS) {
      this._buffer = this._buffer.slice(-MAX_BUFFERED_EVENTS);
    }
  }

  /**
   * The route as declared, e.g. `/clients/:id` rather than `/clients/1567`.
   *
   * Taken from the router's own config by walking the activated snapshot, not by pattern-matching
   * the URL: a regex that replaces digit segments breaks the moment an id is not numeric, and the
   * router already holds the exact answer.
   */
  private _normalisedPath(): string {
    const segments: string[] = [];
    let node: ActivatedRouteSnapshot | null =
      this._router.routerState.snapshot.root;

    while (node) {
      const configured = node.routeConfig?.path;
      if (configured) {
        segments.push(configured);
      }
      node = node.firstChild;
    }

    return segments.length ? `/${segments.join('/')}` : '/';
  }

  /** A readable label, so the report is not a list of route templates. */
  private _pageName(path: string, url: string): string {
    const source = path === '/' ? url : path;
    const words = source
      .split('/')
      .filter((part) => part && !part.startsWith(':'))
      .map((part) => part.split('?')[0])
      .filter(Boolean);

    if (!words.length) {
      return 'Dashboard';
    }
    const last = words[words.length - 1];
    return last.replace(/-/g, ' ').replace(/^./, (char) => char.toUpperCase());
  }

  /* -------------------- delivery -------------------- */

  /**
   * Sends whatever is buffered.
   *
   * The buffer is cleared before the request goes out and restored if it fails, so a slow response
   * cannot cause the same events to be sent twice by the next timer tick. Every event carries a
   * uid anyway, so a genuine double-send is deduplicated server-side.
   */
  private _flush(onUnload: boolean): void {
    if (!this._buffer.length || (this._flushing && !onUnload)) {
      return;
    }

    const events = this._buffer;
    this._buffer = [];
    const body = JSON.stringify({
      sessionKey: this._sessionKey(),
      events,
    });

    if (onUnload) {
      this._sendOnUnload(API_ENDPOINTS.SESSION_ANALYTICS.sessionEvents, body);
      return;
    }

    this._flushing = true;
    this._httpService
      .post(API_ENDPOINTS.SESSION_ANALYTICS.sessionEvents, JSON.parse(body))
      .subscribe({
        next: () => (this._flushing = false),
        error: () => {
          // Put them back for the next tick rather than losing the trail.
          this._buffer = [...events, ...this._buffer].slice(
            -MAX_BUFFERED_EVENTS
          );
          this._flushing = false;
        },
      });
  }

  /**
   * Posts during page teardown.
   *
   * `fetch` with `keepalive` rather than `navigator.sendBeacon`: beacon cannot set headers, so it
   * cannot carry `Authorization: Bearer`, and the whole point of this endpoint is that identity
   * comes from the token rather than the body. `keepalive` requests survive unload too, with a
   * 64 KB cap a batch of page views never approaches.
   */
  private _sendOnUnload(endpoint: string, body: string): void {
    const token = this._token();
    if (!token) {
      return;
    }
    try {
      const base = resolveLocalBaseUrl(
        ServiceType.DEFAULT,
        environment.local.apiUrls.default
      );
      const path = resolveLocalPath(ServiceType.DEFAULT, endpoint);
      void fetch(`${base}${path}`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
      }).catch(() => undefined);
    } catch {
      // Teardown is not a place to surface errors.
    }
  }

  private _token(): string | null {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
      return raw ? (JSON.parse(raw)?.idToken ?? null) : null;
    } catch {
      return null;
    }
  }

  /* -------------------- small helpers -------------------- */

  private _uuid(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    // Older Safari and any non-secure context: good enough for an id that only has to be unique.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }

  private _deviceType(ua: string): string {
    if (/iPad|Tablet/i.test(ua)) {
      return 'tablet';
    }
    return /Mobi|Android|iPhone/i.test(ua) ? 'mobile' : 'desktop';
  }

  private _browser(ua: string): string {
    if (/Edg\//.test(ua)) {
      return 'Edge';
    }
    if (/OPR\//.test(ua)) {
      return 'Opera';
    }
    if (/Chrome\//.test(ua)) {
      return 'Chrome';
    }
    if (/Safari\//.test(ua)) {
      return 'Safari';
    }
    return /Firefox\//.test(ua) ? 'Firefox' : 'Other';
  }

  private _os(ua: string): string {
    if (/Windows/.test(ua)) {
      return 'Windows';
    }
    if (/Mac OS X/.test(ua)) {
      return 'macOS';
    }
    if (/Android/.test(ua)) {
      return 'Android';
    }
    if (/iPhone|iPad|iOS/.test(ua)) {
      return 'iOS';
    }
    return /Linux/.test(ua) ? 'Linux' : 'Other';
  }
}
